import { useState, useCallback, useMemo } from 'react';
import { Sheet, Button, Badge, lucideIcons, SearchInput, Skeleton } from '@round/creatorbase-ui';
import { creatorbase, influencer } from '@round/api';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import AddToCampaignTable from './AddToCampaignTable/AddToCampaignTable';
import CampaignHeader from './Header/CampaignHeader';
import { useCreatorExplore } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplore';
import { useCreatorExplorePlatformSelection } from 'Modules/Plans/CreatorSearch/hooks/useCreatorExplorePlatformSelection';
import { FilterSidebar } from 'Modules/Plans/CreatorSearch/components/FilterSidebar/FilterSidebar';
import { mapFiltersToApiParams, getActiveFilterCount } from 'Modules/Plans/CreatorSearch/utils/filters';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import {
    mapSortToApiParams,
    mapPersistedCampaignFilterParamsToExploreFilters,
    mapExploreFiltersToPersistedCampaignFilterParams,
    AddToCampaignFilters,
} from './helpers';
import { PAGE_SIZE, DATE_ADDED_COLUMN_ID } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { useProjectPostsData } from './useProjectPostsData';
import styles from './AddToCampaignModal.module.css';
import { SortingState } from '@tanstack/react-table';
import CreatorDetailsModal from 'Modules/Plans/CreatorSearch/components/CreatorDetailsModal/CreatorDetailsModal';
import { AddPostToCampaign } from './AddToCampaignTable/components/AddPostCell/AddPostCell';
import { useClientInfluencer } from '@round/creatorbase-helpers/api-hooks';
import { getPlatformCost } from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';
import { Meta } from './AddToCampaignTable/helpers';
import { ModalConfig } from '../../../../CreatorSearch/components/CreatorDetailsModal/helpers';
import useUpdateProjectCampaign from '../../hooks/useUpdateProjectCampaign';

const { Filter } = lucideIcons;

const DEFAULT_SORTING: SortingState = [{ id: DATE_ADDED_COLUMN_ID, desc: true }];

type CreatorModalConfig = ModalConfig & {
    clientInfluencerId: string | null;
};

type Props = {
    isOpen: boolean;
    onClose: (hasUpdated: boolean) => void;
    campaign: creatorbase.TiktokCampaign | creatorbase.InstagramCampaign | undefined;
    projectId: number | null | undefined;
};

const AddToCampaignModal = ({ isOpen, onClose, campaign, projectId }: Props) => {
    const { update: updateCampaign } = useUpdateProjectCampaign();
    const { selectedClientId } = useClientSelect();

    const [selectedUserModalConfig, setSelectedUserModalConfig] = useState<CreatorModalConfig | null>(null);

    const [filters, setFilters] = useState<AddToCampaignFilters>(() =>
        mapPersistedCampaignFilterParamsToExploreFilters(campaign?.creator_explore_filters || {})
    );

    const [showFilters, setShowFilters] = useState(false);
    const activeFilterCount = getActiveFilterCount(filters);

    const [search, setSearch] = useState('');

    const {
        campaigns,
        posts,
        isLoading: isLoadingProjectPosts,
        isFetching: isFetchingProjectPosts,
    } = useProjectPostsData(projectId, { enabled: isOpen });

    const [hasUpdated, setHasUpdated] = useState(false);

    const [addedThisSessionPostIds, setAddedThisSessionPostIds] = useState<Set<string>>(() => new Set());

    const handlePostCreated = useCallback((postId: string) => {
        setAddedThisSessionPostIds((prev) => new Set(prev).add(postId));
    }, []);

    const handlePostDeleted = useCallback((postId: string) => {
        setAddedThisSessionPostIds((prev) => {
            const next = new Set(prev);
            next.delete(postId);
            return next;
        });
    }, []);

    const handleClose = () => {
        onClose(hasUpdated);
        setHasUpdated(false);
        setAddedThisSessionPostIds(new Set());
    };

    const debouncedUpdateCampaign = useMemo(
        () =>
            debounce(
                (id: number, patch: Partial<creatorbase.PatchCampaignBody>) => updateCampaign({ id, ...patch }),
                2000
            ),
        [updateCampaign]
    );

    const { getRowSelectedPlatform, onRowPlatformSelect, resetSelectedPlatforms } =
        useCreatorExplorePlatformSelection();

    const handleUpdateFilters = (filters: AddToCampaignFilters) => {
        setFilters(filters);
        resetSelectedPlatforms();
        if (!campaign?.id) {
            return;
        }

        debouncedUpdateCampaign(campaign?.id, {
            creator_explore_filters: mapExploreFiltersToPersistedCampaignFilterParams(filters),
        });
    };

    const getCreatorPostData = useCallback(
        (clientInfluencerAccountId: number) => {
            const creatorPostsInProject = posts?.filter(
                (p) => p.client_influencer_account_id === clientInfluencerAccountId
            );
            const otherCampaignsWithCreator = campaigns?.filter(
                (c) => c.id !== campaign?.id && creatorPostsInProject?.map((p) => p.campaign_id).includes(c.id)
            );
            const campaignPost = creatorPostsInProject?.find((p) => p.campaign_id === campaign?.id);
            return {
                campaignPost,
                otherCampaignsWithCreator: otherCampaignsWithCreator ?? [],
            };
        },
        [posts, campaigns, campaign?.id]
    );

    const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

    const {
        data,
        getCreatorImages,
        getAreCreatorImagesLoading,
        isLoading,
        hasError,
        count,
        fetchNextPage,
        invalidate: invalidateCreatorExplore,
    } = useCreatorExplore(
        {
            page_size: PAGE_SIZE,
            client_id: selectedClientId,
            search,
            ...mapSortToApiParams(sorting),
            ...mapFiltersToApiParams(filters),
            platforms: campaign?.platform,
        },
        { enabled: isOpen && typeof selectedClientId === 'number' }
    );

    const handleCloseUserModal = useCallback(
        (hasUpdated: boolean) => {
            setSelectedUserModalConfig(null);
            if (hasUpdated) {
                invalidateCreatorExplore();
            }
        },
        [invalidateCreatorExplore]
    );

    const handleCreatorRowClick = useCallback(
        (creator: influencer.CreatorSearchResult) => {
            if (!campaign?.platform) {
                return;
            }

            const account = creator.platforms?.[campaign.platform]?.[0];
            if (!account) {
                return;
            }

            setSelectedUserModalConfig({
                clientInfluencerAccountId: account.creatorbase_client_influencer_account_id,
                platformUserId: account.creatorbase_user_id,
                clientInfluencerId: creator.creatorbase_client_influencer_id,
            });
        },
        [campaign]
    );

    const meta: Meta = {
        isLoading,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading,
        getCreatorPostData,
        getRowSelectedPlatform: (row) => getRowSelectedPlatform(row, campaign?.platform),
        onRowPlatformSelect,
        platform: campaign?.platform,
        projectId,
        campaign,
        onUpdate: () => setHasUpdated(true),
        isLoadingPostData: isLoadingProjectPosts,
        areActionsDisabled: isFetchingProjectPosts,
        addedPostIds: addedThisSessionPostIds,
        onPostCreated: handlePostCreated,
        onPostDeleted: handlePostDeleted,
    };

    return (
        <>
            <Sheet
                open={isOpen}
                onOpenChange={(_, event) => {
                    if (
                        event.reason === 'outside-press' &&
                        !(event.event.currentTarget as HTMLElement).attributes
                            .getNamedItem('data-slot')
                            ?.value.includes('overlay')
                    ) {
                        return;
                    }
                    handleClose();
                }}
            >
                <Sheet.Content side="bottom" className={styles.sheet}>
                    <Sheet.Header className={cn(styles.section, styles.header)}>
                        <CampaignHeader campaign={campaign} onClose={handleClose} />
                    </Sheet.Header>
                    <div className={cn(styles.section, styles.toolbar)}>
                        <Button
                            variant="outline"
                            size="xs"
                            className={styles.filterButton}
                            onClick={() => setShowFilters((prev) => !prev)}
                        >
                            <Filter className={styles.filterButtonIcon} />
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                            {!showFilters && activeFilterCount > 0 && <Badge size="sm">{activeFilterCount}</Badge>}
                        </Button>
                        <SearchInput
                            placeholder="Search creators..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={styles.search}
                            debounce={700}
                        />
                        <span className={styles.countLabel}>
                            {isLoading ? (
                                <Skeleton className={styles.countSkeleton} />
                            ) : typeof count === 'number' ? (
                                `${count} creator${count !== 1 ? 's' : ''}`
                            ) : null}
                        </span>
                    </div>
                    <div className={styles.contentRow}>
                        {showFilters && (
                            <aside className={styles.sidebar}>
                                <FilterSidebar
                                    filters={{
                                        ...filters,
                                        platforms: campaign?.platform ? { conditions: [campaign.platform] } : undefined,
                                    }}
                                    onFiltersChange={handleUpdateFilters}
                                    disabledFilters={{ platforms: true }}
                                />
                            </aside>
                        )}
                        <section className={cn(styles.section, styles.tableSection)}>
                            <AddToCampaignTable
                                data={data}
                                meta={meta}
                                sorting={sorting}
                                onSortingChange={setSorting}
                                fetchNextPage={fetchNextPage}
                                onRowClick={handleCreatorRowClick}
                                className={styles.tableContainer}
                            />
                        </section>
                    </div>
                </Sheet.Content>
            </Sheet>

            <CreatorDetailsModal
                key={selectedUserModalConfig?.clientInfluencerAccountId}
                isOpen={!!selectedUserModalConfig}
                onClose={handleCloseUserModal}
                clientInfluencerAccountId={selectedUserModalConfig?.clientInfluencerAccountId}
                contextTools={function ModalAddPost() {
                    const { data: clientInfluencer, isFetching } = useClientInfluencer(
                        selectedUserModalConfig?.clientInfluencerId,
                        {
                            enabled: !!selectedUserModalConfig?.clientInfluencerId,
                        }
                    );

                    return (
                        <AddPostToCampaign
                            creatorData={
                                selectedUserModalConfig && campaign?.platform
                                    ? {
                                          clientInfluencerAccountId: selectedUserModalConfig.clientInfluencerAccountId,
                                          ...getPlatformCost(clientInfluencer, campaign.platform),
                                      }
                                    : null
                            }
                            getCreatorPostData={getCreatorPostData}
                            isLoading={isLoadingProjectPosts}
                            areActionsDisabled={isFetchingProjectPosts || isFetching}
                            projectId={projectId}
                            campaign={campaign}
                            onUpdate={() => setHasUpdated(true)}
                            addedPostIds={addedThisSessionPostIds}
                            onPostCreated={handlePostCreated}
                            onPostDeleted={handlePostDeleted}
                        />
                    );
                }}
            />
        </>
    );
};

export default AddToCampaignModal;
